"use client";

import type { License } from "@/api/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useLicenses, useLicenseStatistics, useReactivateLicense, useRevokeLicense } from "@/hooks/use-licenses";
import { formatDate, formatDateLong, getDaysRemaining } from "@/lib/utils";
import { useOrgStore, useUserStore } from "@/stores";
import { Check, Copy, Eye, EyeOff, Plus, ShieldCheck, Trash2 } from "lucide-react";
import { useState } from "react";

import { LicenseGenerateDialog } from "./license-generate-dialog";

export function LicensesView() {
  const user = useUserStore.use.user();
  const selectedOrgId = useOrgStore.use.selectedOrgId();
  const selectedOrg = useOrgStore.use.selectedOrg();
  const orgs = useOrgStore.use.orgs();

  // For non-super-admins, only query orgs that appear in token permissions (i.e. orgs the backend
  // will accept). The org store's selectedOrgId is persisted to localStorage and may point to an
  // org the user no longer has token-level access to, causing every license endpoint to 403.
  const accessibleOrgIds = user?.permissions?.map((p) => p.id) ?? [];
  const orgIdForQuery = user?.superAdmin
    ? selectedOrgId || undefined
    : selectedOrgId && accessibleOrgIds.includes(selectedOrgId)
      ? selectedOrgId
      : (accessibleOrgIds[0] ?? user?.defaultOrg?.id);

  const selectedOrgIsAccessible = orgIdForQuery ? accessibleOrgIds.includes(orgIdForQuery) : false;
  const isAdmin =
    user?.superAdmin ||
    user?.permissions?.some(
      (perm) =>
        (selectedOrgIsAccessible ? perm.id === orgIdForQuery : perm.default) && perm.roles?.includes("ORGOWNER"),
    );
  const licensesQuery = useLicenses(orgIdForQuery);
  const statisticsQuery = useLicenseStatistics(orgIdForQuery);
  const revokeMutation = useRevokeLicense();
  const reactivateMutation = useReactivateLicense();

  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState<License | null>(null);
  const [licenseKeyRevealed, setLicenseKeyRevealed] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedHolder, setCopiedHolder] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [copiedJson, setCopiedJson] = useState(false);
  const [confirmRevokeId, setConfirmRevokeId] = useState<number | null>(null);
  const [confirmRevokeInDialog, setConfirmRevokeInDialog] = useState(false);

  const licenses = licensesQuery.data ?? [];
  const statistics = statisticsQuery.data;
  const isLoading = licensesQuery.isLoading || statisticsQuery.isLoading;

  const getStatusBadge = (license: License) => {
    if (!license.is_active) {
      return <Badge variant="destructive">Inactive</Badge>;
    }
    const daysRemaining = getDaysRemaining(license.expiration_date);
    if (daysRemaining < 0) {
      return <Badge variant="destructive">Expired</Badge>;
    }
    if (daysRemaining < 30) {
      return <Badge variant="secondary">Expiring Soon</Badge>;
    }
    return <Badge variant="default">Active</Badge>;
  };

  const copyToClipboard = async (text: string, type: "key" | "holder") => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === "key") {
        setCopiedKey(true);
        setTimeout(() => setCopiedKey(false), 2000);
      } else {
        setCopiedHolder(true);
        setTimeout(() => setCopiedHolder(false), 2000);
      }
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  };

  const copyFieldToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  };

  const copyAsJson = async (license: License) => {
    try {
      const jsonData = {
        license_key: license.license_key,
        license_holder: license.license_holder,
        organization: license.organization_name,
        organization_id: license.organization_id,
        status: license.is_active ? "Active" : "Inactive",
        created_date: license.created_at,
        expiration_date: license.expiration_date,
        created_by: license.created_by_user_email,
        notes: license.notes || "",
      };
      await navigator.clipboard.writeText(JSON.stringify(jsonData, null, 2));
      setCopiedJson(true);
      setTimeout(() => setCopiedJson(false), 2000);
    } catch (error) {
      console.error("Failed to copy JSON:", error);
    }
  };

  const handleRevoke = async (license: License) => {
    try {
      const updated = await revokeMutation.mutateAsync(license.id);
      if (selectedLicense?.id === license.id) setSelectedLicense(updated);
    } catch (error) {
      console.error("Failed to revoke license:", error);
    } finally {
      setConfirmRevokeId(null);
      setConfirmRevokeInDialog(false);
    }
  };

  const handleReactivate = async (license: License) => {
    const updated = await reactivateMutation.mutateAsync(license.id);
    if (selectedLicense?.id === license.id) setSelectedLicense(updated);
  };

  // User has org access if they're super admin OR have organizations
  const hasOrgAccess = user?.superAdmin || (user?.organizations && user.organizations.length > 0);

  if (!hasOrgAccess) {
    return (
      <div className="flex h-full items-center justify-center">
        <Card className="w-96">
          <CardHeader>
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>You do not have permission to view licenses.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">License Management</h1>
          <p className="text-muted-foreground">
            {isAdmin
              ? "Generate and manage Istari Platform licenses for your organization"
              : "View your licenses and licenses in your organization"}
          </p>
          {orgs.length > 1 && selectedOrg && (
            <div className="mt-2 flex items-center gap-2">
              <Badge variant="outline" className="text-xs">
                Viewing: {selectedOrg.name}
              </Badge>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button onClick={() => setShowGenerateDialog(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Generate License
            </Button>
          )}
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Licenses</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">{statistics?.total_licenses || 0}</div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Active Licenses</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">{statistics?.active_licenses || 0}</div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Expired Licenses</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">{statistics?.expired_licenses || 0}</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Licenses Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Licenses</CardTitle>
          <CardDescription>View and manage all generated licenses</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>License Key</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead>Creation Date</TableHead>
                  <TableHead>Expiration</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {licenses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground">
                      No licenses found. Generate your first license to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  licenses.map((license) => (
                    <TableRow key={license.id}>
                      <TableCell className="font-mono text-xs">…{license.license_key.slice(-7)}</TableCell>
                      <TableCell className="max-w-xs truncate" title={license.notes || undefined}>
                        {license.notes || <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-help">{formatDate(license.created_at)}</span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Created: {formatDateLong(license.created_at)}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-help">{formatDate(license.expiration_date)}</span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Expires: {formatDateLong(license.expiration_date)}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{getStatusBadge(license)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedLicense(license);
                              setLicenseKeyRevealed(false);
                              setConfirmRevokeId(null);
                            }}
                          >
                            View
                          </Button>
                          {isAdmin &&
                            (confirmRevokeId === license.id ? (
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-muted-foreground">Revoke?</span>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => handleRevoke(license)}
                                  disabled={revokeMutation.isPending}
                                >
                                  Yes
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => setConfirmRevokeId(null)}>
                                  No
                                </Button>
                              </div>
                            ) : license.is_active ? (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                onClick={() => setConfirmRevokeId(license.id)}
                                aria-label="Revoke license"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-green-600 hover:bg-green-50 hover:text-green-700 dark:hover:bg-green-950/20"
                                onClick={() => handleReactivate(license)}
                                disabled={reactivateMutation.isPending}
                              >
                                <ShieldCheck className="mr-1 h-3 w-3" />
                                Reactivate
                              </Button>
                            ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <LicenseGenerateDialog
        open={showGenerateDialog}
        onOpenChange={setShowGenerateDialog}
        defaultOrgId={orgIdForQuery}
      />

      {/* License Details Dialog */}
      {selectedLicense && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => setSelectedLicense(null)}
        >
          <Card className="m-4 w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>License Details</CardTitle>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => copyAsJson(selectedLicense)}>
                    {copiedJson ? (
                      <>
                        <Check className="mr-1 h-4 w-4 text-green-500" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="mr-1 h-4 w-4" />
                        Copy as JSON
                      </>
                    )}
                  </Button>
                  {isAdmin &&
                    (confirmRevokeInDialog ? (
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-muted-foreground">Are you sure?</span>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleRevoke(selectedLicense)}
                          disabled={revokeMutation.isPending}
                        >
                          Yes, Revoke
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setConfirmRevokeInDialog(false)}>
                          Cancel
                        </Button>
                      </div>
                    ) : selectedLicense.is_active ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-destructive text-destructive hover:bg-destructive/10"
                        onClick={() => setConfirmRevokeInDialog(true)}
                      >
                        <Trash2 className="mr-1 h-4 w-4" />
                        Revoke
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-green-600 text-green-600 hover:bg-green-50 dark:hover:bg-green-950/20"
                        onClick={() => handleReactivate(selectedLicense)}
                        disabled={reactivateMutation.isPending}
                      >
                        <ShieldCheck className="mr-1 h-4 w-4" />
                        Reactivate
                      </Button>
                    ))}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setConfirmRevokeInDialog(false);
                      setSelectedLicense(null);
                    }}
                  >
                    ✕
                  </Button>
                </div>
              </div>
              {selectedLicense.is_custom_organization && (
                <div className="mt-3 rounded-lg border-2 border-amber-400 bg-amber-50 p-3 dark:border-amber-600 dark:bg-amber-950/20">
                  <div className="flex items-start gap-2">
                    <span className="text-xl">⚠️</span>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                        Custom Organization - User Not Yet in Portal
                      </p>
                      <p className="mt-1 text-xs text-amber-800 dark:text-amber-200">
                        The organization "<strong>{selectedLicense.organization_name}</strong>" and/or the license
                        holder do not exist in the customer portal yet. The Organization ID shown below is the{" "}
                        <strong>creator's organization ID</strong>, not the custom organization's ID.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {/* License Holder - Moved to top */}
              <div className="rounded border-l-4 border-blue-500 bg-blue-50 py-3 pl-4 dark:bg-blue-950/20">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-muted-foreground">License Holder</h3>
                    <p className="mt-1 break-all font-mono text-sm">{selectedLicense.license_holder}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Required for license validation - cryptographically encoded in key
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => copyToClipboard(selectedLicense.license_holder, "holder")}
                    className="shrink-0"
                  >
                    {copiedHolder ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              {/* License Key - with reveal functionality */}
              <div className="rounded border-l-4 border-green-500 bg-green-50 py-3 pl-4 dark:bg-green-950/20">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-medium text-muted-foreground">License Key</h3>
                      {licenseKeyRevealed && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setLicenseKeyRevealed(false)}
                          className="h-6 gap-1 px-2 py-0 text-xs"
                        >
                          <EyeOff className="h-3 w-3" />
                          Click to hide
                        </Button>
                      )}
                    </div>
                    {licenseKeyRevealed ? (
                      <p className="mt-1 break-all font-mono text-sm">{selectedLicense.license_key}</p>
                    ) : (
                      <div className="mt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setLicenseKeyRevealed(true)}
                          className="gap-2"
                        >
                          <Eye className="h-4 w-4" />
                          Click to reveal license key
                        </Button>
                      </div>
                    )}
                  </div>
                  {licenseKeyRevealed && (
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => copyToClipboard(selectedLicense.license_key, "key")}
                      className="shrink-0"
                    >
                      {copiedKey ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-muted-foreground">Organization</h3>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => copyFieldToClipboard(selectedLicense.organization_name, "org")}
                    >
                      {copiedField === "org" ? (
                        <Check className="h-3 w-3 text-green-500" />
                      ) : (
                        <Copy className="h-3 w-3" />
                      )}
                    </Button>
                  </div>
                  <p className="mt-1">{selectedLicense.organization_name}</p>
                  <div className="mt-2 flex items-center gap-1">
                    <span className="text-xs text-muted-foreground">
                      {selectedLicense.is_custom_organization ? "Creator's Org ID:" : "Organization ID:"}
                    </span>
                    <code className="text-xs">{selectedLicense.organization_id}</code>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-4 w-4"
                      onClick={() => copyFieldToClipboard(selectedLicense.organization_id, "orgId")}
                    >
                      {copiedField === "orgId" ? (
                        <Check className="h-2 w-2 text-green-500" />
                      ) : (
                        <Copy className="h-2 w-2" />
                      )}
                    </Button>
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Status</h3>
                  <div className="mt-1">{getStatusBadge(selectedLicense)}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-muted-foreground">Created Date</h3>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => copyFieldToClipboard(formatDate(selectedLicense.created_at), "created")}
                    >
                      {copiedField === "created" ? (
                        <Check className="h-3 w-3 text-green-500" />
                      ) : (
                        <Copy className="h-3 w-3" />
                      )}
                    </Button>
                  </div>
                  <p className="mt-1">{formatDate(selectedLicense.created_at)}</p>
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-muted-foreground">Expiration Date</h3>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => copyFieldToClipboard(formatDate(selectedLicense.expiration_date), "expiration")}
                    >
                      {copiedField === "expiration" ? (
                        <Check className="h-3 w-3 text-green-500" />
                      ) : (
                        <Copy className="h-3 w-3" />
                      )}
                    </Button>
                  </div>
                  <p className="mt-1">{formatDate(selectedLicense.expiration_date)}</p>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-muted-foreground">Created By</h3>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => copyFieldToClipboard(selectedLicense.created_by_user_email, "createdBy")}
                  >
                    {copiedField === "createdBy" ? (
                      <Check className="h-3 w-3 text-green-500" />
                    ) : (
                      <Copy className="h-3 w-3" />
                    )}
                  </Button>
                </div>
                <p className="mt-1">{selectedLicense.created_by_user_email}</p>
              </div>
              {selectedLicense.notes && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Notes</h3>
                  <p className="mt-1">{selectedLicense.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
